/**
 * [T149 / Calendar-Insight follow-up] SpendingHeatmapCalendarSkin —
 *
 * Adapts the canonical CalendarMatrix primitive to the FinTrack SkinProps
 * contract. Source files: ../primitives/CalendarMatrix (controlled popover,
 * M3 follow-up additions).
 */
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { CalendarMatrix, type CalendarMatrixDay } from '../../primitives/CalendarMatrix';
import type { SkinProps } from '../types';
import type { HeatmapPayload } from '@/lib/insightsContracts';
import { computeHeatmapIntensities } from '@/lib/insightsContracts';

export type { CalendarMatrixDay };

const MOBILE_BREAKPOINT_PX = 768;

interface SpendingHeatmapCalendarSkinProps extends SkinProps<'spending-heatmap-calendar'> {
  onViewTransactions?: (date: string, returnFocus: () => void) => void;
}

/**
 * Hook returning true when viewport < MOBILE_BREAKPOINT_PX. SSR-safe
 * default to non-mobile when window is undefined.
 */
function useIsMobile(): boolean {
  const [w, setW] = useState<number>(
    typeof window === 'undefined' ? Infinity : window.innerWidth,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w < MOBILE_BREAKPOINT_PX;
}

/**
 * Map backend `HeatmapPayload.days` into the CalendarMatrix primitive's day
 * shape (`date / total / count / intensity`). Tolerates empty payloads.
 */
function toMatrixDays(data: HeatmapPayload | null | undefined): CalendarMatrixDay[] {
  if (!data || !data.days) return [];
  return computeHeatmapIntensities(data.days).map((d) => ({
    date: d.date,
    total: d.total,
    count: d.count,
    intensity: d.intensity,
  }));
}

/**
 * Spending-Heatmap-Calendar skin — accepts the canonical SkinProps contract
 * (widget / kind / data / loading / SkinStateProps).
 */
export function SpendingHeatmapCalendarSkin(props: SpendingHeatmapCalendarSkinProps) {
  const { data, loading, onViewTransactions } = props;
  const isMobile = useIsMobile();
  const orientation = (isMobile ? 'vertical' : 'horizontal') as
    | 'vertical'
    | 'horizontal';

  const [activeDate, setActiveDate] = useState<string | null>(null);

  const handleActiveDateChange = useCallback((key: string | null) => {
    setActiveDate(key);
  }, []);

  const days = useMemo(() => toMatrixDays(data as HeatmapPayload | null | undefined), [data]);

  return (
    <div
      data-orientation={orientation}
      data-active-cell-popover={activeDate ?? ''}
      data-loading={loading ? 'true' : 'false'}
      className="w-full"
    >
      <CalendarMatrix
        days={days}
        orientation={orientation}
        onViewTransactions={onViewTransactions}
        activeDate={activeDate}
        onActiveDateChange={handleActiveDateChange}
      />
    </div>
  );
}

export default SpendingHeatmapCalendarSkin;
